import { t, type TranslationKeys } from '@signage-player/renderer-ui';
import { navigateToPicker } from './instanceHistory';
import { enterFullscreen, onFullscreenPending } from './webFullscreen';
import { browserFamily, installState, onInstallChange, promptInstall, soundPermanentKey, wasInstalledHere } from './webInstall';
import { isPlayerMedia, mutedPlayback, onSoundChange, playerMedia, soundStatus, unlockSound } from './webSound';

interface IssueAction {
  label: string;
  run: () => void;
}

interface Issue {
  id: string;
  title: string;
  why: string;
  fix: string;
  /** Verbatim URL / file name shown under the title. */
  detail?: string;
  kind?: 'warning' | 'info';
  /** actions[0] renders as the primary button. */
  actions?: IssueAction[];
}

const active = new Map<string, Issue>();
// Cleared issues stay listed as resolved while the panel is open, so a fix made
// from the panel (or by the very click that opened it) is visible feedback.
const resolved = new Map<string, Issue>();
let badge: HTMLButtonElement | null = null;
let panel: HTMLElement | null = null;

/**
 * Yellow "!" badge (top-left) listing what keeps content from playing as
 * configured — blocked sound / video autoplay, a media file the browser can't
 * play, content refused by the page's CSP — each with the reason and the fix.
 * Sites that refuse to be framed (X-Frame-Options) are not observable here.
 */
export function installWebIssues(): void {
  injectStyles();
  badge = document.createElement('button');
  badge.type = 'button';
  badge.className = 'web-issues-badge hidden';
  badge.textContent = '!';
  badge.setAttribute('aria-label', t('issues_badge_label'));
  badge.setAttribute('aria-haspopup', 'dialog');
  badge.setAttribute('aria-expanded', 'false');
  badge.addEventListener('click', () => (panel ? closePanel() : openPanel()));
  document.body.appendChild(badge);

  document.addEventListener(
    'pointerdown',
    e => {
      if (panel && e.target !== badge && !panel.contains(e.target as Node)) closePanel();
    },
    true
  );
  window.addEventListener(
    'keydown',
    e => {
      if (!panel || e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      closePanel();
    },
    true
  );

  watchSound();
  hookPlay();
  watchMediaErrors();
  watchCsp();
  watchContentSrc();
  watchInstall();
  watchFullscreen();
}

function report(issue: Issue): void {
  active.set(issue.id, issue);
  resolved.delete(issue.id);
  render();
}

function clear(id: string): void {
  const issue = active.get(id);
  if (!issue) return;
  active.delete(id);
  if (panel) resolved.set(id, issue);
  render();
}

function render(): void {
  if (!badge) return;
  badge.classList.toggle('hidden', active.size === 0 && !panel);
  const infoOnly = active.size > 0 && Array.from(active.values()).every(i => i.kind === 'info');
  badge.classList.toggle('info-only', infoOnly);
  badge.textContent = infoOnly ? 'i' : '!';
  badge.title = Array.from(active.values(), i => i.title).join('\n');
  if (panel) renderPanel();
}

// ---- detectors -------------------------------------------------------------

function soundIssue(): Issue {
  // Only Chromium lifts the autoplay block for an installed app window.
  const installable = browserFamily() === 'chromium' && installState() === 'promptable';
  return {
    id: 'sound',
    title: t('issue_sound_title'),
    why: t('issue_sound_why'),
    fix: `${t('issue_sound_fix')} ${t(soundPermanentKey())}`,
    actions: installable
      ? [
          { label: t('issue_sound_action_install'), run: () => void promptInstall() },
          { label: t('issue_sound_action_enable_now'), run: unlockSound },
        ]
      : [{ label: t('settings_sound_enable'), run: unlockSound }],
  };
}

function videoIssue(): Issue {
  return {
    id: 'video',
    title: t('issue_video_title'),
    why: t('issue_video_why'),
    fix: t('issue_video_fix'),
    actions: [
      {
        label: t('issue_video_action'),
        // Inside the click: a gesture-backed play() is what Low Power Mode wants.
        run: () => {
          for (const el of playerMedia()) {
            if (el.paused && el.getAttribute('src') && !el.classList.contains('hidden')) {
              el.play().catch(() => {
                /* reported by the play hook */
              });
            }
          }
        },
      },
    ],
  };
}

function watchSound(): void {
  let timer: number | null = null;
  const evaluate = () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      timer = null;
      const status = await soundStatus();
      if (status === 'blocked' || mutedPlayback()) report(soundIssue());
      else clear('sound');
    }, 250);
  };
  onSoundChange(evaluate);
  // Install state decides both the fix text and the buttons of the sound entry.
  onInstallChange(evaluate);
  for (const type of ['play', 'playing', 'pause', 'volumechange', 'emptied', 'ended']) {
    document.addEventListener(type, e => isPlayerMedia(e.target) && evaluate(), true);
  }
  // Any interaction flips Chromium's sticky activation — re-check after it.
  window.addEventListener('pointerdown', evaluate, { capture: true, passive: true });
  window.addEventListener('keydown', evaluate, { capture: true, passive: true });
  evaluate();
}

// Observes the renderer's own play() attempts: a NotAllowedError on a muted
// element means autoplay is blocked outright (iOS Low Power Mode, data saver).
function hookPlay(): void {
  const original = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function (this: HTMLMediaElement): Promise<void> {
    const result = original.apply(this);
    if (isPlayerMedia(this) && typeof result?.then === 'function') {
      result.then(
        () => clear('video'),
        (err: unknown) => {
          if ((err as { name?: string } | null)?.name !== 'NotAllowedError') return;
          report(this.muted ? videoIssue() : soundIssue());
        }
      );
    }
    return result;
  };
}

function fileLabel(el: HTMLMediaElement): string {
  const src = el.currentSrc || el.src;
  try {
    const name = decodeURIComponent(new URL(src).pathname.split('/').pop() ?? '');
    if (name) return name;
  } catch {
    /* not a URL */
  }
  return src.length > 80 ? `${src.slice(0, 77)}…` : src;
}

function watchMediaErrors(): void {
  document.addEventListener(
    'error',
    e => {
      const el = e.target;
      // The static buffer elements ship with src="" — that "error" is not a file problem.
      if (!isPlayerMedia(el) || !el.error || !el.getAttribute('src')) return;
      const code = el.error.code;
      if (code === MediaError.MEDIA_ERR_ABORTED) return;
      const why: TranslationKeys =
        code === MediaError.MEDIA_ERR_NETWORK
          ? 'issue_media_why_network'
          : code === MediaError.MEDIA_ERR_DECODE
            ? 'issue_media_why_decode'
            : 'issue_media_why_unsupported';
      report({ id: `media:${el.id}`, title: t('issue_media_title'), why: t(why), fix: t('issue_media_fix'), detail: fileLabel(el) });
    },
    true
  );
  for (const type of ['playing', 'emptied']) {
    document.addEventListener(type, e => isPlayerMedia(e.target) && clear(`media:${e.target.id}`), true);
  }
}

// The installing tab keeps playing next to the new app window — double playback
// and double check-ins for the same instance until this tab stops.
function watchInstall(): void {
  onInstallChange(() => {
    if (!wasInstalledHere()) return;
    report({
      id: 'installed',
      kind: 'info',
      title: t('issue_installed_title'),
      why: t('issue_installed_why'),
      fix: t('issue_installed_fix'),
      actions: [{ label: t('issue_installed_action_stop'), run: navigateToPicker }],
    });
  });
}

// Start in fullscreen is on but the browser wants a gesture — webFullscreen arms
// the first click; this entry says so and offers the click.
function watchFullscreen(): void {
  onFullscreenPending(pending => {
    if (!pending) {
      clear('fullscreen');
      return;
    }
    report({
      id: 'fullscreen',
      kind: 'info',
      title: t('issue_fullscreen_title'),
      why: t('issue_fullscreen_why'),
      fix: t('issue_fullscreen_fix'),
      actions: [{ label: t('issue_fullscreen_action'), run: () => void enterFullscreen() }],
    });
  });
}

// src attribute changes on player content: a media element moving on drops its
// error (removeAttribute fires no "emptied"), and a CSP entry goes once nothing
// references its URL any more (nothing signals "unblocked").
function watchContentSrc(): void {
  const main = document.getElementById('main-container');
  if (!main) return;
  new MutationObserver(records => {
    for (const r of records) {
      if (r.target instanceof HTMLMediaElement) clear(`media:${r.target.id}`);
    }
    const srcs = contentSrcs(main);
    for (const id of Array.from(active.keys())) {
      if (id.startsWith('csp:') && !srcs.some(s => s.startsWith(id.slice(4)))) clear(id);
    }
  }).observe(main, { subtree: true, attributes: true, attributeFilter: ['src'] });
}

function contentSrcs(main: HTMLElement): string[] {
  return Array.from(main.querySelectorAll('iframe, video, audio, img'), el => (el.getAttribute('src') ?? '').split('#')[0]);
}

const CSP_CONTENT_DIRECTIVES = new Set(['frame-src', 'media-src', 'img-src']);

function watchCsp(): void {
  const main = document.getElementById('main-container');
  if (!main) return;
  // Chrome fires the event at the document (not the element) and strips a
  // blocked frame URL to its origin — attribute it by directive + URL prefix.
  document.addEventListener(
    'securitypolicyviolation',
    e => {
      const uri = e.blockedURI;
      const directive = e.effectiveDirective || e.violatedDirective;
      if (!CSP_CONTENT_DIRECTIVES.has(directive) || !/^https?:/.test(uri)) return;
      const src = contentSrcs(main).find(s => s.startsWith(uri));
      if (!src) return;
      report({
        id: `csp:${uri}`,
        title: t('issue_csp_title'),
        why: t('issue_csp_why'),
        fix: t(uri.startsWith('http:') ? 'issue_csp_fix_http' : 'issue_csp_fix'),
        detail: `${src} (${directive})`,
      });
    },
    true
  );
}

// ---- panel -----------------------------------------------------------------

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

function para(labelKey: TranslationKeys, text: string): HTMLElement {
  const p = h('p');
  p.append(h('b', undefined, `${t(labelKey)} `), text);
  return p;
}

function openPanel(): void {
  if (panel || (active.size === 0 && resolved.size === 0)) return;
  panel = h('div', 'web-issues-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', t('issues_title'));
  document.body.appendChild(panel);
  badge?.setAttribute('aria-expanded', 'true');
  renderPanel();
}

function closePanel(): void {
  if (!panel) return;
  panel.remove();
  panel = null;
  resolved.clear();
  badge?.setAttribute('aria-expanded', 'false');
  render();
}

function renderPanel(): void {
  if (!panel) return;
  panel.replaceChildren();
  const head = h('div', 'wi-head');
  const close = h('button', 'wi-close', '×');
  close.type = 'button';
  close.setAttribute('aria-label', t('settings_close'));
  close.addEventListener('click', closePanel);
  head.append(h('h3', undefined, t('issues_title')), close);
  panel.appendChild(head);

  const entries: Array<[Issue, boolean]> = [
    ...Array.from(active.values(), (i): [Issue, boolean] => [i, false]),
    ...Array.from(resolved.values(), (i): [Issue, boolean] => [i, true]),
  ];
  for (const [issue, done] of entries) {
    const classes = ['wi-item'];
    if (done) classes.push('wi-resolved');
    else if (issue.kind === 'info') classes.push('wi-info');
    const item = h('div', classes.join(' '));
    item.appendChild(h('div', 'wi-title', issue.title));
    if (issue.detail) item.appendChild(h('div', 'wi-detail', issue.detail));
    item.append(para('issues_why', issue.why), para('issues_fix', issue.fix));
    if (done) {
      item.appendChild(h('div', 'wi-status', t('issues_resolved')));
    } else if (issue.actions?.length) {
      const actions = h('div', 'wi-actions');
      for (const [index, action] of issue.actions.entries()) {
        const btn = h('button', index === 0 ? 'ws-btn wi-primary' : 'ws-btn', action.label);
        btn.type = 'button';
        btn.addEventListener('click', action.run);
        actions.appendChild(btn);
      }
      item.appendChild(actions);
    }
    panel.appendChild(item);
  }
}

// ---- styles ----------------------------------------------------------------

function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    .web-issues-badge { position: fixed; top: 35px; left: 43px; z-index: 9100; width: 30px; height: 30px; padding: 0;
      border: 0; border-radius: 50%; background: #f5b83d; color: #1a1200; text-align: center;
      font: 700 18px/30px 'Poppins', system-ui, sans-serif; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35); }
    .web-issues-badge:hover { background: #ffc75a; }
    .web-issues-badge.info-only { background: #5da7ff; color: #06172e; }
    .web-issues-badge.info-only:hover { background: #7fbaff; }
    .web-issues-badge:focus-visible { outline: 2px solid #ffc75a; outline-offset: 2px; }
    .web-issues-badge.hidden { display: none; }
    .web-issues-panel { position: fixed; top: 75px; left: 43px; z-index: 9100; box-sizing: border-box; width: 420px;
      max-width: calc(100% - 32px); max-height: calc(100% - 91px); overflow-y: auto; padding: 12px 18px 14px;
      border: 1px solid #2c3a4d; border-radius: 12px; background: #0d1626; color: #e8e8e8;
      font-family: 'Poppins', system-ui, sans-serif; font-size: 14px; line-height: 1.45;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45); }
    /* The pairing screen's back button owns the top-left corner — sit under it. */
    body.web:has(#device-id-container:not(.hidden)) .web-issues-badge { top: 90px; }
    body.web:has(#device-id-container:not(.hidden)) .web-issues-panel { top: 130px; }
    .web-issues-panel, .web-issues-panel * { cursor: default; }
    .web-issues-panel button, .web-issues-panel button * { cursor: pointer; }
    .wi-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 6px; }
    .wi-head h3 { margin: 0; font-size: 16px; font-weight: 600; }
    .wi-close { width: 30px; height: 30px; padding: 0; border: 1px solid #2c3a4d; border-radius: 8px; background: #0d1626;
      color: #e8e8e8; font-size: 18px; line-height: 1; }
    .wi-close:hover { border-color: #3989eb; background: #14203a; color: #5da7ff; }
    .wi-item { padding: 12px 0 6px; border-top: 1px solid #2c3a4d; }
    .wi-head + .wi-item { border-top: 0; padding-top: 4px; }
    .wi-title { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; }
    .wi-title::before { content: '!'; flex: none; width: 20px; height: 20px; border-radius: 50%; background: #f5b83d;
      color: #1a1200; font-size: 13px; line-height: 20px; text-align: center; }
    .wi-resolved .wi-title::before { content: '✓'; background: #6ac258; color: #06210a; }
    .wi-detail { margin: 4px 0 0 28px; font-size: 12px; color: #afafaf; overflow-wrap: anywhere;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
    .wi-item p { margin: 8px 0 0 28px; color: #cfd2d6; }
    .wi-item p b { color: #e8e8e8; font-weight: 600; }
    .wi-actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0 4px 28px; }
    .wi-actions .ws-btn { margin: 0; }
    .wi-primary { border-color: #3989eb; }
    .wi-info .wi-title::before { content: 'i'; background: #5da7ff; color: #06172e; }
    .wi-status { margin: 8px 0 4px 28px; color: #6ac258; font-weight: 600; }
    @media screen and (max-width: 950px) {
      .web-issues-badge { top: 16px; left: 16px; }
      .web-issues-panel { top: 56px; left: 16px; max-height: calc(100% - 72px); }
      body.web:has(#device-id-container:not(.hidden)) .web-issues-badge { top: 69px; }
      body.web:has(#device-id-container:not(.hidden)) .web-issues-panel { top: 109px; }
    }
    @media (prefers-color-scheme: light) {
      .web-issues-panel { background: #ffffff; color: #181818; border-color: #c7c9cf; box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2); }
      .wi-close { color: #181818; border-color: #c7c9cf; background: #ffffff; }
      .wi-close:hover { color: #1f75de; border-color: #1f75de; background: #ffffff; }
      .wi-item { border-color: #e3e5e9; }
      .wi-item p { color: #3a3d42; }
      .wi-item p b { color: #181818; }
      .wi-detail { color: #5a5d63; }
      .wi-status { color: #2f8f3a; }
      .wi-primary { border-color: #1f75de; }
    }
  `;
  document.head.appendChild(style);
}
